import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import type { ReactNode } from 'react'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30s stale time — data is fresh enough for most browsing.
      // Auth-sensitive queries override this with staleTime: 0.
      staleTime: 30_000,
      // Retry failed requests twice with backoff (Axios also retries on network errors).
      // 4xx responses are not retried — they are real errors.
      retry: 2,
      retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 30_000),
    },
    mutations: {
      // Mutations do not retry — ledger operations are not idempotent.
      retry: false,
    },
  },
})

interface QueryProviderProps {
  children: ReactNode
}

// Opt-in only — the devtools panel persists its own open/closed state in
// localStorage once ever opened, so `initialIsOpen={false}` alone doesn't
// keep the floating toggle button off screen. Enable with:
//   localStorage.setItem('swim-show-devtools', '1')
// then reload. Remove the same way.
const SHOW_DEVTOOLS =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  window.localStorage.getItem('swim-show-devtools') === '1'

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {SHOW_DEVTOOLS && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}
